import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const FilterContext = createContext();
const STORAGE_KEY = 'metua_active_filters_v1';

const DEFAULT_SELECTION = {
  region: null,
  departement: null,
  arrondissement: null,
  quartier: null,
  zoneInteret: null,
  periode: 'Last day',
  vehicleType: null,
};

const safeReadStorage = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const isReloadNavigation = () => {
  try {
    if (typeof window === 'undefined' || typeof performance === 'undefined') return false;

    const navEntries = performance.getEntriesByType?.('navigation');
    if (Array.isArray(navEntries) && navEntries.length > 0) {
      return navEntries[0]?.type === 'reload';
    }

    if (window.performance?.navigation) {
      return window.performance.navigation.type === 1;
    }
  } catch {
    // ignore detection errors
  }

  return false;
};

const sanitizePersistedStateOnReload = (state) => {
  if (!state || !isReloadNavigation()) return state;

  const allowedTypes = new Set(['period', 'vehicle']);
  const nextFilters = (Array.isArray(state.activeFilters) ? state.activeFilters : []).filter((f) =>
    allowedTypes.has(f?.type)
  );

  return {
    activeFilters: nextFilters,
    currentSelection: rebuildSelection(nextFilters),
  };
};

const mapFilterToSelection = (selection, filter) => {
  if (!filter) return selection;

  const next = { ...selection };
  const value = filter.label || filter.value || null;

  if (filter.type === 'vehicle') {
    next.vehicleType = value;
    return next;
  }

  if (filter.type === 'period') {
    next.periode = value || DEFAULT_SELECTION.periode;
    return next;
  }

  if (filter.type === 'site') {
    if (filter.value === 'all') {
      next.region = null;
    } else {
      next.region = value;
    }
    return next;
  }

  if (filter.type === 'special-item' || String(filter.type || '').startsWith('special-item-')) {
    switch (filter.category) {
      case 'administrative-zones':
        next.region = value;
        break;
      case 'departement':
        next.departement = value;
        break;
      case 'arrondissement':
        next.arrondissement = value;
        break;
      case 'quartier':
        next.quartier = value;
        break;
      case 'lieu':
        next.zoneInteret = value;
        break;
      default:
        break;
    }
  }

  return next;
};

const rebuildSelection = (filters) => {
  return filters.reduce((acc, filter) => mapFilterToSelection(acc, filter), {
    ...DEFAULT_SELECTION,
  });
};

const buildSelectedAdministrativeIds = (filters = []) => {
  const ids = {
    regionsids: [],
    departementids: [],
    arrondissementids: [],
    groupementids: [],
    quartierids: [],
    zoneids: [],
  };

  const mapCategoryToKey = {
    'administrative-zones': 'regionsids',
    departement: 'departementids',
    arrondissement: 'arrondissementids',
    district: 'groupementids',
    quartier: 'quartierids',
    lieu: 'zoneids',
  };

  (Array.isArray(filters) ? filters : []).forEach((filter) => {
    const type = String(filter?.type || '');
    const isSpecial = type === 'special-item' || type.startsWith('special-item-');
    if (!isSpecial) return;
    const key = mapCategoryToKey[filter?.category];
    if (!key) return;
    if (filter?.itemId == null) return;
    ids[key].push(filter.itemId);
  });

  Object.keys(ids).forEach((key) => {
    ids[key] = [...new Set(ids[key])];
  });

  return ids;
};

export const useActiveFilters = () => {
  const context = useContext(FilterContext);
  if (!context) {
    throw new Error('useActiveFilters must be used within a FilterProvider');
  }
  return context;
};

export const FilterProvider = ({ children }) => {
  const initial = sanitizePersistedStateOnReload(safeReadStorage());
  const [activeFilters, setActiveFilters] = useState(initial?.activeFilters || []);
  const [currentSelection, setCurrentSelection] = useState(
    initial?.currentSelection || rebuildSelection(initial?.activeFilters || [])
  );
  const selectedAdministrativeIds = useMemo(
    () => buildSelectedAdministrativeIds(activeFilters),
    [activeFilters]
  );

  const persist = (filters, selection) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ activeFilters: filters, currentSelection: selection })
      );
    } catch {
      // ignore storage errors
    }
  };

  const addFilter = (filter) => {
    setActiveFilters((prev) => {
      const updatedFilters = prev.filter((f) => f.type !== filter.type);
      const nextFilters = [...updatedFilters, filter];

      setCurrentSelection((prevSelection) => {
        const nextSelection = mapFilterToSelection(prevSelection, filter);
        persist(nextFilters, nextSelection);
        return nextSelection;
      });

      return nextFilters;
    });
  };

  const addFilters = (filtersToAdd = []) => {
    if (!Array.isArray(filtersToAdd) || filtersToAdd.length === 0) return;

    setActiveFilters((prev) => {
      const nextByType = new Map(prev.map((f) => [f.type, f]));
      filtersToAdd.forEach((filter) => {
        if (!filter || !filter.type) return;
        nextByType.set(filter.type, filter);
      });

      const nextFilters = Array.from(nextByType.values());
      const nextSelection = rebuildSelection(nextFilters);
      setCurrentSelection(nextSelection);
      persist(nextFilters, nextSelection);
      return nextFilters;
    });
  };

  const removeFilter = (filterToRemove) => {
    setActiveFilters((prev) => {
      const nextFilters = prev.filter(
        (f) => !(f.type === filterToRemove.type && f.value === filterToRemove.value)
      );
      const nextSelection = rebuildSelection(nextFilters);
      setCurrentSelection(nextSelection);
      persist(nextFilters, nextSelection);
      return nextFilters;
    });
  };

  const removeFilters = (filtersToRemove = []) => {
    if (!Array.isArray(filtersToRemove) || filtersToRemove.length === 0) return;

    const keysToRemove = new Set(
      filtersToRemove.map((f) => `${f?.type ?? ''}::${f?.value ?? ''}`)
    );

    setActiveFilters((prev) => {
      const nextFilters = prev.filter(
        (f) => !keysToRemove.has(`${f?.type ?? ''}::${f?.value ?? ''}`)
      );
      const nextSelection = rebuildSelection(nextFilters);
      setCurrentSelection(nextSelection);
      persist(nextFilters, nextSelection);
      return nextFilters;
    });
  };

  const clearFilters = () => {
    const nextFilters = [];
    const nextSelection = { ...DEFAULT_SELECTION };
    setActiveFilters(nextFilters);
    setCurrentSelection(nextSelection);
    persist(nextFilters, nextSelection);
  };

  const setPeriode = (label) => addFilter({ type: 'period', value: label, label });
  const setVehicleType = (label) => addFilter({ type: 'vehicle', value: label, label });

  useEffect(() => {
    // Persist cleaned state immediately after reload sanitization.
    if (isReloadNavigation()) {
      persist(activeFilters, currentSelection);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(
    () => ({
      activeFilters,
      currentSelection,
      addFilter,
      addFilters,
      removeFilter,
      removeFilters,
      clearFilters,
      setPeriode,
      setVehicleType,
      selectedAdministrativeIds,
    }),
    [activeFilters, currentSelection, selectedAdministrativeIds]
  );

  return <FilterContext.Provider value={value}>{children}</FilterContext.Provider>;
};
